package com.havn.app.features.followups.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.EmptyState
import com.havn.app.core.designsystem.HavnBackground
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnCardBorder
import com.havn.app.core.designsystem.HavnGold
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnIvory
import com.havn.app.core.designsystem.HavnMetric
import com.havn.app.core.designsystem.HavnMuted
import com.havn.app.core.designsystem.HavnPushNavigation
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.HavnSurfaceElevated
import com.havn.app.core.designsystem.HavnTextField
import com.havn.app.core.designsystem.LoadingState
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.features.followups.domain.FollowUp
import com.havn.app.features.followups.domain.FollowUpChannel
import com.havn.app.features.followups.domain.FollowUpStatus
import com.havn.app.features.leads.domain.Lead
import com.havn.app.ui.theme.LocalHavnThemeTokens

@Composable
fun FollowUpsScreen(
    viewModel: FollowUpsViewModel,
    onBackToLeadDetail: () -> Unit = {},
    onFollowUpCompleted: () -> Unit = {},
    canUseAiFollowUps: Boolean = true,
    onAiUpgradeRequired: () -> Unit = {}
) {
    val state by viewModel.uiState.collectAsState()
    val completedFollowUpEventId = state.completedFollowUpEventId

    LaunchedEffect(completedFollowUpEventId) {
        if (completedFollowUpEventId != null) {
            onFollowUpCompleted()
            viewModel.consumeCompletedFollowUpEvent()
        }
    }

    HavnPushNavigation(
        targetState = state.mode,
        depthFor = FollowUpsMode::navigationDepth
    ) { mode ->
        HavnScreenFrame(
            eyebrow = "",
            title = when (mode) {
                FollowUpsMode.Queue -> "Follow-ups"
                FollowUpsMode.Generate -> "Send Follow Up"
                FollowUpsMode.Preview -> "Preview follow-up"
            },
            subtitle = when (mode) {
                FollowUpsMode.Queue -> "Create polished SMS and email drafts from lead context."
                FollowUpsMode.Generate -> ""
                FollowUpsMode.Preview -> "Review the generated draft before saving."
            },
            metrics = if (mode == FollowUpsMode.Queue) {
                listOf(
                    HavnMetric("Drafts", state.pendingCount.toString(), "pending"),
                    HavnMetric("Sent", state.sentCount.toString(), "manual"),
                    HavnMetric("Leads", state.leads.size.toString(), "available")
                )
            } else {
                emptyList()
            },
            backLabel = when (mode) {
                FollowUpsMode.Queue -> null
                FollowUpsMode.Generate -> "Lead"
                FollowUpsMode.Preview -> "Generate Draft"
            },
            onBack = when (mode) {
                FollowUpsMode.Queue -> null
                FollowUpsMode.Generate -> onBackToLeadDetail
                FollowUpsMode.Preview -> viewModel::openGenerator
            },
            isRefreshing = state.isLoading,
            onRefresh = viewModel::refresh
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
                state.error?.let { message ->
                    HavnCard {
                        Text(message, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyLarge)
                        Spacer(Modifier.height(10.dp))
                        HavnButton(
                            text = "Dismiss",
                            onClick = viewModel::clearError,
                            style = HavnButtonStyle.Ghost,
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }

                when (mode) {
                    FollowUpsMode.Queue -> FollowUpQueueScreen(state, viewModel)
                    FollowUpsMode.Generate -> FollowUpComposerContent(
                        state = state,
                        viewModel = viewModel,
                        canUseAiAssistant = canUseAiFollowUps,
                        onAiUpgradeRequired = onAiUpgradeRequired
                    )
                    FollowUpsMode.Preview -> FollowUpPreviewScreen(state, viewModel)
                }
            }
        }
    }
}

@Composable
fun FollowUpComposerContent(
    state: FollowUpsUiState,
    viewModel: FollowUpsViewModel,
    showLeadSummary: Boolean = true,
    showAiAssistant: Boolean = true,
    canUseAiAssistant: Boolean = true,
    onAiUpgradeRequired: () -> Unit = {}
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        if (showLeadSummary) state.selectedLead?.let { lead ->
            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text(lead.fullName, color = theme.primaryText, style = MaterialTheme.typography.headlineSmall)
                    Text(lead.sourceProperty.title, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
                    Text(lead.contactLine, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                }
            }
        }

        HavnCard {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Text(strings.leadFollowUp, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                Column(verticalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                        MessageTypeChip(strings.followUpMessageTypeThankYou, selected = state.selectedMessageType == FollowUpMessageType.ThankYou, modifier = Modifier.weight(1f)) {
                            viewModel.selectMessageType(FollowUpMessageType.ThankYou)
                        }
                        MessageTypeChip(strings.followUpMessageTypePropertyInfo, selected = state.selectedMessageType == FollowUpMessageType.PropertyInfo, modifier = Modifier.weight(1f)) {
                            viewModel.selectMessageType(FollowUpMessageType.PropertyInfo)
                        }
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                        MessageTypeChip(strings.followUpMessageTypeFollowUp, selected = state.selectedMessageType == FollowUpMessageType.FollowUp, modifier = Modifier.weight(1f)) {
                            viewModel.selectMessageType(FollowUpMessageType.FollowUp)
                        }
                        MessageTypeChip(strings.followUpMessageTypeCheckIn, selected = state.selectedMessageType == FollowUpMessageType.CheckIn, modifier = Modifier.weight(1f)) {
                            viewModel.selectMessageType(FollowUpMessageType.CheckIn)
                        }
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                        MessageTypeChip(strings.followUpMessageTypePriceUpdate, selected = state.selectedMessageType == FollowUpMessageType.PriceUpdate, modifier = Modifier.weight(1f)) {
                            viewModel.selectMessageType(FollowUpMessageType.PriceUpdate)
                        }
                        MessageTypeChip(strings.followUpMessageTypePropertyBrochure, selected = state.selectedMessageType == FollowUpMessageType.PropertyBrochure, modifier = Modifier.weight(1f)) {
                            viewModel.selectMessageType(FollowUpMessageType.PropertyBrochure)
                        }
                    }
                }
                if (state.showPhotoSelection) {
                    FollowUpPhotoSelector(
                        photos = state.availablePhotoDataUrls,
                        selectedPhotos = state.selectedPhotoDataUrls,
                        onTogglePhoto = viewModel::toggleSelectedPhoto
                    )
                }
                if (showAiAssistant) {
                    AiAssistantCard(
                        isGenerating = state.isGenerating,
                        canGenerate = canUseAiAssistant,
                        onGenerate = if (canUseAiAssistant) {
                            viewModel::generateInlineDraft
                        } else {
                            onAiUpgradeRequired
                        }
                    )
                }
            }
        }

        HavnCard {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text(strings.followUpPreview, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                HavnTextField(
                    value = state.emailSubject,
                    onValueChange = viewModel::updatePreviewSubject,
                    label = strings.followUpSubject,
                    leadingIcon = HavnIconKey.Mail
                )
                HavnTextField(
                    value = state.sendFollowUpPreview,
                    onValueChange = viewModel::updatePreviewBody,
                    label = strings.followUpMessage,
                    singleLine = false
                )
            }
        }

        HavnButton(
            text = strings.followUpSendViaEmail,
            onClick = viewModel::sendSelectedDraftEmail,
            loading = state.savingChannel == FollowUpChannel.Email,
            modifier = Modifier.fillMaxWidth(),
            leadingIcon = HavnIconKey.Mail
        )
        if (!state.selectedLead?.phone.isNullOrBlank()) {
            HavnButton(
                text = strings.followUpSendViaSms,
                onClick = viewModel::sendSelectedDraftSms,
                loading = state.savingChannel == FollowUpChannel.Sms,
                modifier = Modifier.fillMaxWidth(),
                style = HavnButtonStyle.Secondary,
                leadingIcon = HavnIconKey.Message
            )
        }

        state.error?.let { message ->
            Text(message, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyMedium)
        }
    }
}

@Composable
private fun FollowUpPhotoSelector(
    photos: List<String>,
    selectedPhotos: List<String>,
    onTogglePhoto: (String) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(strings.photosTitle, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            Text(
                "${selectedPhotos.distinct().size}/${photos.distinct().size}",
                color = theme.secondaryText,
                style = MaterialTheme.typography.labelLarge
            )
        }
        LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            itemsIndexed(photos.distinct(), key = { _, photo -> photo }) { index, photo ->
                val selected = photo in selectedPhotos
                Surface(
                    modifier = Modifier
                        .size(width = 86.dp, height = 72.dp)
                        .clickable { onTogglePhoto(photo) },
                    shape = RoundedCornerShape(14.dp),
                    color = theme.surface,
                    border = BorderStroke(
                        width = if (selected) 2.dp else 1.dp,
                        color = if (selected) theme.primaryAccent else theme.border
                    )
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        val rendered = HavnDatabaseImage(
                            dataUrl = photo,
                            contentDescription = "${strings.photo} ${index + 1}",
                            modifier = Modifier.fillMaxSize(),
                            contentScale = ContentScale.Crop
                        )
                        if (!rendered) {
                            Box(
                                Modifier
                                    .fillMaxSize()
                                    .background(Brush.linearGradient(listOf(theme.surface, theme.primaryAccent.copy(alpha = 0.18f), theme.background)))
                            )
                            HavnIcon(HavnIconKey.Home, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(20.dp))
                        }
                        if (selected) {
                            Surface(
                                modifier = Modifier
                                    .align(Alignment.TopEnd)
                                    .padding(6.dp)
                                    .size(22.dp),
                                shape = RoundedCornerShape(11.dp),
                                color = theme.primaryAccent,
                                contentColor = theme.background
                            ) {
                                Box(contentAlignment = Alignment.Center) {
                                    HavnIcon(HavnIconKey.Check, contentDescription = null, tint = theme.background, modifier = Modifier.size(14.dp))
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AiAssistantCard(
    isGenerating: Boolean,
    canGenerate: Boolean,
    onGenerate: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(14.dp),
        color = Color.Transparent,
        border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.72f))
    ) {
        Box(
            modifier = Modifier
                .background(
                    Brush.horizontalGradient(
                        listOf(
                            theme.surface.copy(alpha = 0.88f),
                            theme.card.copy(alpha = 0.78f),
                            theme.primaryAccent.copy(alpha = 0.12f)
                        )
                    )
                )
                .padding(16.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                        HavnIcon(HavnIconKey.Star, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(22.dp))
                        Text(
                            strings.followUpAiAssistantTitle,
                            color = theme.primaryText,
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.SemiBold
                        )
                    }
                    Text(
                        strings.followUpAiAssistantBody,
                        color = theme.secondaryText,
                        style = MaterialTheme.typography.bodyMedium
                    )
                    HavnButton(
                        text = if (canGenerate) strings.followUpGenerateWithAi else strings.followUpUpgradeForAi,
                        onClick = onGenerate,
                        loading = isGenerating,
                        leadingIcon = HavnIconKey.Send
                    )
                }
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    HavnIcon(HavnIconKey.Star, contentDescription = null, tint = theme.primaryAccent.copy(alpha = 0.92f), modifier = Modifier.size(28.dp))
                    HavnIcon(HavnIconKey.Star, contentDescription = null, tint = theme.primaryAccent.copy(alpha = 0.58f), modifier = Modifier.size(48.dp))
                    HavnIcon(HavnIconKey.Star, contentDescription = null, tint = theme.primaryAccent.copy(alpha = 0.78f), modifier = Modifier.size(24.dp))
                }
            }
        }
    }
}

@Composable
private fun MessageTypeChip(
    text: String,
    selected: Boolean,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val shape = RoundedCornerShape(8.dp)
    Surface(
        modifier = modifier
            .height(52.dp)
            .clip(shape)
            .clickable(onClick = onClick),
        shape = shape,
        color = if (selected) Color.Transparent else theme.surface.copy(alpha = 0.62f),
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border)
    ) {
        Box(
            contentAlignment = Alignment.Center,
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 8.dp)
        ) {
            Text(
                text = text,
                color = theme.primaryText,
                style = MaterialTheme.typography.labelSmall,
                fontWeight = FontWeight.Medium,
                textAlign = androidx.compose.ui.text.style.TextAlign.Center,
                maxLines = 2
            )
        }
    }
}

@Composable
private fun FollowUpQueueScreen(
    state: FollowUpsUiState,
    viewModel: FollowUpsViewModel
) {
    HavnButton(
        text = "Write follow-up",
        onClick = viewModel::openGenerator,
        modifier = Modifier.fillMaxWidth(),
        leadingIcon = HavnIconKey.Plus
    )

    if (state.isLoading) {
        LoadingState("Loading follow-up queue")
    } else if (state.followUps.isEmpty()) {
        EmptyState(
            title = "No follow-ups yet",
            body = "Generate a draft from lead context, save it, then mark it sent when you send it manually.",
            actionText = "Create draft",
            onAction = viewModel::openGenerator
        )
    } else {
        HavnTextField(
            value = state.form.completedAt,
            onValueChange = { value -> viewModel.updateForm { copy(completedAt = value) } },
            label = "Sent at",
            placeholder = "2026-05-19T12:00:00Z",
            leadingIcon = HavnIconKey.Calendar
        )
        state.followUps.forEach { followUp ->
            FollowUpQueueRow(
                followUp = followUp,
                onMarkSent = { viewModel.markSent(followUp.id, state.form.completedAt) },
                enabled = !state.isSaving
            )
        }
    }
}

@Composable
fun GenerateFollowUpScreen(
    state: FollowUpsUiState,
    viewModel: FollowUpsViewModel
) {
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text("Choose lead", color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            if (state.leads.isEmpty()) {
                Text("No leads available yet.", color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
            } else {
                state.leads.forEach { lead ->
                    LeadPickerRow(
                        lead = lead,
                        selected = state.selectedLeadId == lead.id,
                        onClick = { viewModel.selectLead(lead.id) }
                    )
                }
            }
        }
    }

    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            HavnTextField(
                value = state.form.visitorName,
                onValueChange = { value -> viewModel.updateForm { copy(visitorName = value) } },
                label = "Lead name",
                leadingIcon = HavnIconKey.User
            )
            HavnTextField(
                value = state.form.propertyAddress,
                onValueChange = { value -> viewModel.updateForm { copy(propertyAddress = value) } },
                label = "Property address",
                leadingIcon = HavnIconKey.Properties
            )
            HavnTextField(
                value = state.form.visitorInterest,
                onValueChange = { value -> viewModel.updateForm { copy(visitorInterest = value) } },
                label = "Lead interest",
                placeholder = "Buying, investing, relocating",
                leadingIcon = HavnIconKey.Search
            )
            HavnTextField(
                value = state.form.leadStatus,
                onValueChange = { value -> viewModel.updateForm { copy(leadStatus = value) } },
                label = "Lead status",
                placeholder = "Hot, Warm, Cold"
            )
            HavnTextField(
                value = state.form.notes,
                onValueChange = { value -> viewModel.updateForm { copy(notes = value) } },
                label = "Agent notes",
                placeholder = "Motivation, preferences, timing, objections",
                singleLine = false
            )
            HavnTextField(
                value = state.form.dueAt,
                onValueChange = { value -> viewModel.updateForm { copy(dueAt = value) } },
                label = "Draft due time",
                placeholder = "2026-05-20T09:00:00Z",
                leadingIcon = HavnIconKey.Calendar
            )
            HavnTextField(
                value = state.form.completedAt,
                onValueChange = { value -> viewModel.updateForm { copy(completedAt = value) } },
                label = "Sent time",
                placeholder = "2026-05-19T12:00:00Z",
                leadingIcon = HavnIconKey.Check
            )
            HavnButton(
                text = "Generate drafts",
                onClick = viewModel::generateDraft,
                loading = state.isGenerating,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Mail
            )
        }
    }
}

@Composable
fun FollowUpPreviewScreen(
    state: FollowUpsUiState,
    viewModel: FollowUpsViewModel
) {
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text("Draft type", color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
                ChannelButton(
                    text = "SMS",
                    selected = state.previewChannel == FollowUpChannel.Sms,
                    onClick = { viewModel.selectPreviewChannel(FollowUpChannel.Sms) },
                    modifier = Modifier.weight(1f)
                )
                ChannelButton(
                    text = "Email",
                    selected = state.previewChannel == FollowUpChannel.Email,
                    onClick = { viewModel.selectPreviewChannel(FollowUpChannel.Email) },
                    modifier = Modifier.weight(1f)
                )
            }
            HavnTextField(
                value = state.selectedPreviewBody,
                onValueChange = viewModel::updatePreviewBody,
                label = if (state.previewChannel == FollowUpChannel.Email) "Email draft" else "SMS draft",
                singleLine = false
            )
            HavnButton(
                text = "Save draft",
                onClick = viewModel::saveSelectedDraft,
                loading = state.isSaving,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Check
            )
            HavnButton(
                text = "Mark saved draft sent",
                onClick = viewModel::markSavedDraftSent,
                enabled = state.savedFollowUpId != null,
                loading = state.isSaving,
                style = HavnButtonStyle.Secondary,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun LeadPickerRow(
    lead: Lead,
    selected: Boolean,
    onClick: () -> Unit
) {
    HavnButton(
        text = "${lead.fullName} · ${lead.sourceProperty.title}",
        onClick = onClick,
        style = if (selected) HavnButtonStyle.Primary else HavnButtonStyle.Secondary,
        modifier = Modifier.fillMaxWidth(),
        leadingIcon = HavnIconKey.User
    )
}

@Composable
private fun FollowUpQueueRow(
    followUp: FollowUp,
    onMarkSent: () -> Unit,
    enabled: Boolean
) {
    val theme = LocalHavnThemeTokens.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
                Column(Modifier.weight(1f)) {
                    Text(followUp.subject, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                    Text(
                        "${followUp.channel.label} · ${followUp.status.label} · due ${followUp.dueAt.compactDateTime()}",
                        color = theme.primaryAccent,
                        style = MaterialTheme.typography.labelLarge
                    )
                }
            }
            followUp.body?.takeIf { it.isNotBlank() }?.let {
                Text(it, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium, maxLines = 3, overflow = TextOverflow.Ellipsis)
            }
            if (followUp.status != FollowUpStatus.Completed) {
                HavnButton(
                    text = "Mark sent",
                    onClick = onMarkSent,
                    enabled = enabled,
                    style = HavnButtonStyle.Secondary,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
}

@Composable
private fun ChannelButton(
    text: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    HavnButton(
        text = text,
        onClick = onClick,
        style = if (selected) HavnButtonStyle.Primary else HavnButtonStyle.Secondary,
        modifier = modifier
    )
}

private fun String.compactDateTime(): String {
    if (isBlank()) return "not scheduled"
    return replace("T", " ")
        .substringBefore(".")
        .substringBefore("+")
        .substringBefore("Z")
}

private fun FollowUpsMode.navigationDepth(): Int = when (this) {
    FollowUpsMode.Queue -> 0
    FollowUpsMode.Generate -> 1
    FollowUpsMode.Preview -> 2
}
